import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { partnerPortalApi } from '../../api/partnerPortal';

/**
 * The real, branded email-first router - the whole point of this
 * page. A visitor types their email; the server tells us (via
 * checkEmail, which never leaks pending/rejected/suspended status)
 * whether it belongs to an approved Partner account. If so, the
 * password field appears right here. If not, we assume staff and
 * hand off to the existing, unchanged Entra flow - VConnect's one
 * real internal identity provider, exactly as it worked before this
 * page existed.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'partner-password'>('email');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, getValues, formState: { isSubmitting } } = useForm<{ email: string; password: string }>();

  const onContinue = async (values: { email: string }) => {
    setError(null);
    setChecking(true);
    try {
      const result = await partnerPortalApi.checkEmail(values.email);
      if (result.data.isPartner) {
        setStep('partner-password');
      } else {
        window.location.href = '/auth/login';
      }
    } catch {
      // If the check itself fails, fail safe toward the existing, proven staff flow rather than blocking the person entirely.
      window.location.href = '/auth/login';
    } finally {
      setChecking(false);
    }
  };

  const onPartnerLogin = async (values: { email: string; password: string }) => {
    setError(null);
    try {
      await partnerPortalApi.login(values);
      navigate('/partner/portal');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in.');
    }
  };

  return (
    <div className="login-page">
      <div className="card" style={{ maxWidth: 440, margin: '80px auto', borderTop: '4px solid var(--vri-green)' }}>
        <h1>Sign in to VConnect</h1>
        <p className="muted">Verdantrise Humanitarian Initiative</p>

        {step === 'email' && (
          <form onSubmit={handleSubmit(onContinue)}>
            <label>Email Address<input type="email" autoFocus {...register('email', { required: true })} /></label>
            {error && <p className="field-error">{error}</p>}
            <div className="form-actions"><button type="submit" disabled={checking}>{checking ? 'Checking…' : 'Continue'}</button></div>
            <p className="muted" style={{ fontSize: 13, marginTop: '1em' }}>
              Verdantrise staff sign in with their Microsoft account. Partner organization staff sign in with the email and password used to register.
            </p>
          </form>
        )}

        {step === 'partner-password' && (
          <form onSubmit={handleSubmit(onPartnerLogin)}>
            <label>Email<input type="email" value={getValues('email')} disabled {...register('email')} /></label>
            <label>Password<input type="password" autoFocus {...register('password', { required: true })} /></label>
            {error && <p className="field-error">{error}</p>}
            <div className="form-actions"><button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Signing in…' : 'Sign In'}</button></div>
            <p className="muted" style={{ fontSize: 13 }}>
              <Link to="/partner/forgot-password">Forgot password?</Link> · <button type="button" className="link-button" onClick={() => setStep('email')}>Use a different email</button>
            </p>
          </form>
        )}

        <p className="muted" style={{ fontSize: 13, marginTop: '1.5em', borderTop: '1px solid var(--color-border)', paddingTop: '1em' }}>
          Partner organization staff without an account yet - <Link to="/partner/register">Register here</Link>.
        </p>
      </div>
    </div>
  );
}
